import React, { useState, useEffect, useCallback } from 'react';
import TimelineFirestoreService from '../../timeline/services/TimelineFirestoreService';
import MapFirestoreService from '../../map/services/MapFirestoreService';
import {
  generateQuestions,
  filterByTags,
  filterByType,
  shuffle,
  getAllTags,
  getAllTypes,
} from '../utils/questionGenerator';
import '../styles/QuizPage.css';

const timelineService = new TimelineFirestoreService();
const mapService = new MapFirestoreService();

function QuizPage() {
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);

  // Filters
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [questionCount, setQuestionCount] = useState(10);

  // Load data on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [events, markers] = await Promise.all([
          timelineService.queryByRange(-4000, 2100),
          mapService.getAllMarkers(),
        ]);
        const questions = generateQuestions(events, markers);
        setAllQuestions(questions);
        setAvailableTags(getAllTags(questions));
        setAvailableTypes(getAllTypes(questions));
      } catch (err) {
        console.error('Failed to load quiz data:', err);
      }
    }
    loadData();
  }, []);

  const startQuiz = () => {
    let filtered = allQuestions;
    filtered = filterByTags(filtered, selectedTags);
    filtered = filterByType(filtered, selectedTypes);
    const shuffled = shuffle(filtered).slice(0, questionCount);

    if (shuffled.length === 0) {
      alert('No questions match your filters. Try broadening your selection.');
      return;
    }

    setFilteredQuestions(shuffled);
    setCurrentIndex(0);
    setShowAnswer(false);
    setScore({ correct: 0, incorrect: 0 });
    setQuizStarted(true);
    setQuizComplete(false);
  };

  const handleReveal = () => setShowAnswer(true);

  const handleMark = (correct) => {
    setScore(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      incorrect: prev.incorrect + (correct ? 0 : 1),
    }));

    if (currentIndex < filteredQuestions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowAnswer(false);
    } else {
      setQuizComplete(true);
    }
  };

  const handleRestart = () => {
    setQuizStarted(false);
    setQuizComplete(false);
    setScore({ correct: 0, incorrect: 0 });
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleType = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const filteredTagList = availableTags.filter(tag =>
    tag.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const currentQuestion = filteredQuestions[currentIndex];

  // Setup screen
  if (!quizStarted) {
    return (
      <div className="quiz-page">
        <h1 className="quiz-title">History Quiz</h1>
        <p className="quiz-subtitle">
          {allQuestions.length} questions available from your timeline & map data
        </p>

        <div className="quiz-setup">
          {/* Question count */}
          <div className="quiz-setup-section">
            <h3>Number of Questions</h3>
            <div className="quiz-count-selector">
              {[5, 10, 20, 50].map(n => (
                <button
                  key={n}
                  className={`quiz-count-btn ${questionCount === n ? 'active' : ''}`}
                  onClick={() => setQuestionCount(n)}
                >
                  {n}
                </button>
              ))}
              <button
                className={`quiz-count-btn ${questionCount === allQuestions.length ? 'active' : ''}`}
                onClick={() => setQuestionCount(allQuestions.length)}
              >
                All
              </button>
            </div>
          </div>

          {/* Type filter */}
          <div className="quiz-setup-section">
            <h3>Question Types</h3>
            <div className="quiz-type-chips">
              {availableTypes.map(type => (
                <button
                  key={type}
                  className={`quiz-chip ${selectedTypes.includes(type) ? 'active' : ''}`}
                  onClick={() => toggleType(type)}
                >
                  {type.replace(/-/g, ' ')}
                </button>
              ))}
            </div>
            {selectedTypes.length === 0 && (
              <p className="quiz-hint">No filter = all types included</p>
            )}
          </div>

          {/* Tag filter */}
          <div className="quiz-setup-section">
            <h3>Filter by Tags</h3>
            <input
              type="text"
              className="quiz-tag-search"
              placeholder="Search tags..."
              value={tagSearch}
              onChange={e => setTagSearch(e.target.value)}
            />
            <div className="quiz-tag-chips">
              {filteredTagList.slice(0, 30).map(tag => (
                <button
                  key={tag}
                  className={`quiz-chip ${selectedTags.includes(tag) ? 'active' : ''}`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
              {filteredTagList.length > 30 && (
                <span className="quiz-hint">+{filteredTagList.length - 30} more</span>
              )}
            </div>
            {selectedTags.length > 0 && (
              <p className="quiz-hint">
                Filtering: {selectedTags.join(', ')}
                <button className="quiz-clear-tags" onClick={() => setSelectedTags([])}>Clear</button>
              </p>
            )}
          </div>

          <button className="quiz-start-btn" onClick={startQuiz}>
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  // Completion screen
  if (quizComplete) {
    const total = score.correct + score.incorrect;
    const percent = Math.round((score.correct / total) * 100);
    return (
      <div className="quiz-page">
        <h1 className="quiz-title">Quiz Complete!</h1>
        <div className="quiz-results">
          <div className="quiz-score">
            <span className="quiz-score-number">{percent}%</span>
            <span className="quiz-score-detail">
              {score.correct} / {total} correct
            </span>
          </div>
          <button className="quiz-restart-btn" onClick={handleRestart}>
            New Quiz
          </button>
        </div>
      </div>
    );
  }

  // Quiz in progress
  return (
    <div className="quiz-page">
      <div className="quiz-header">
        <span className="quiz-progress">
          {currentIndex + 1} / {filteredQuestions.length}
        </span>
        <span className="quiz-running-score">
          ✓ {score.correct} ✗ {score.incorrect}
        </span>
      </div>

      <div className="quiz-card">
        <div className="quiz-question-type">{currentQuestion.type.replace(/-/g, ' ')}</div>
        <h2 className="quiz-question">{currentQuestion.question}</h2>

        {!showAnswer ? (
          <button className="quiz-reveal-btn" onClick={handleReveal}>
            Reveal Answer
          </button>
        ) : (
          <div className="quiz-answer-section">
            <div className="quiz-answer">{currentQuestion.answer}</div>
            <div className="quiz-mark-buttons">
              <button className="quiz-correct-btn" onClick={() => handleMark(true)}>
                ✓ Got it
              </button>
              <button className="quiz-incorrect-btn" onClick={() => handleMark(false)}>
                ✗ Missed it
              </button>
            </div>
          </div>
        )}
      </div>

      {currentQuestion.tags.length > 0 && (
        <div className="quiz-question-tags">
          {currentQuestion.tags.map(tag => (
            <span key={tag} className="quiz-question-tag">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default QuizPage;
